import { store, Model } from "hybrids";

import { get, post, remove } from "../utils/api";

import Comment from "./Comment";
import Course from "./Course";
import Event from "./Event";
import Payment from "./Payment";
import User from "./User";

const URL = "/broadcasts/:id";

export interface Patron {
  user: User;
  amount: number;
}

interface Broadcast {
  // fields
  id: string;
  src: string;
  iframely: string;
  muxDataApiKey: string;
  likes: number;
  availableUntil: string;
  course: Course;
  event: Event;
  users: User[];
  connected: User[];
  comments: Comment[];
  likedComments: Comment[];
  payments: Payment[];
  permissions: {
    canManageAllComments: boolean;
  };

  // computed properties
  patrons: Patron[];
  audience: User[];

  header: string;
}

export function addLikes(broadcast: Broadcast, likes = 1) {
  let diff = likesCounter.get(broadcast.id) || 0;
  if (!diff) {
    setTimeout(() => {
      post(
        `${URL}/likes`,
        { likes: likesCounter.get(broadcast.id) },
        broadcast.id
      );
      likesCounter.delete(broadcast.id);
    }, 1000);
  }

  likesCounter.set(broadcast.id, diff + likes);

  return store.set(broadcast, { likes: broadcast.likes + likes });
}

export async function removeComment(broadcast: Broadcast, comment: Comment) {
  const set = new Set(broadcast.comments);

  set.forEach((c) => {
    if (c === comment || c.parent === comment) {
      set.delete(c);
    }
  });

  store.set(broadcast, { comments: [...set] });
  store.set(comment, null);

  await remove(`${URL}/comments/${comment.id}`, broadcast.id);

  return null;
}

export async function toggleCommentLike(
  broadcast: Broadcast,
  comment: Comment
) {
  const set = new Set(broadcast.likedComments);
  const hasComment = set.has(comment);

  set[hasComment ? "delete" : "add"](comment);
  store.set(broadcast, { likedComments: [...set] });

  const { id, ...values }: Comment = await post(
    `${URL}/comments/${comment.id}/${hasComment ? "dislike" : "like"}`,
    null,
    broadcast.id
  );

  values.context = {
    type: "broadcast",
    identifier: broadcast.id,
  }

  return store.set(comment, values);
}

const likesCounter = new Map();

const Broadcast: Model<Broadcast> = {
  id: true,
  src: "",
  iframely: "",
  muxDataApiKey: "",
  likes: 0,
  availableUntil: "",
  course: Course,
  event: Event,
  users: [User],
  connected: [User],
  comments: store.ref(() => [Comment]),
  likedComments: store.ref(() => [Comment]),
  payments: [Payment],
  permissions: {
    canManageAllComments: false,
  },

  patrons: ({ payments, users, course }) => {
    const acc = new Map<User, Patron>();

    payments
      .filter(({ user }) => store.ready(user))
      .forEach(({ user, amount }) => {
        const patron = { user, amount: (acc.get(user)?.amount ?? 0) + amount };

        acc.set(user, patron);
      });

    users
      .filter(
        (user) =>
          store.ready(user) &&
          user.member &&
          !course.collaborators.includes(user)
      )
      .forEach((user) => {
        const patron = { user, amount: acc.get(user)?.amount ?? 0 };

        acc.set(user, patron);
      });

    return [...acc].map(([_, patron]) => patron);
  },
  audience: ({ users, patrons, course }) => {
    const patronUsers = patrons.map(({ user }) => user);
    return users.filter(
      (user) =>
        !patronUsers.includes(user) && !course.collaborators.includes(user)
    );
  },
  header: ({ course }) => (course.session ? "Artists" : "Collaborators"),

  [store.connect]: {
    get: (id) => get(URL, id),
    set: (_, values) => values,
  },
};

export default Broadcast;
